/* ========================= FORMATO Y PRESENTACIÓN ======================= */

const COP = new Intl.NumberFormat("es-CO", {
  style: "currency",
  currency: "COP",
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const NUM = new Intl.NumberFormat("es-CO");

/**
 * "No hay dato" en cualquiera de sus formas.
 *
 * Hace falta porque `Number(null)` es 0 y `Number(undefined)` es NaN. Sin esto,
 * `money(null)` pintaba "$ 0" y `money(undefined)` pintaba "—": dos formas de
 * decir lo mismo con dos resultados distintos, y la peor de las dos es la que
 * convierte un importe ausente en un cobro de cero pesos.
 */
const sinDato = (v) => v === null || v === undefined || v === "";

/**
 * Pesos colombianos sin decimales. El backend devuelve los montos como
 * decimales en texto ("2500.00"), así que se normaliza antes de formatear:
 * pintarlo crudo es justamente lo que se ve mal en la app móvil.
 */
export const money = (value) => {
  if (sinDato(value)) return "—";
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return COP.format(Math.round(n));
};

/** Igual que `money` pero compacto, para ejes de gráficas. */
export const moneyShort = (value) => {
  const n = Number(value) || 0;
  const abs = Math.abs(n);
  if (abs >= 1_000_000)
    return `$${(n / 1_000_000).toFixed(1).replace(".0", "")}M`;
  if (abs >= 1_000) return `$${Math.round(n / 1_000)}k`;
  return `$${Math.round(n)}`;
};

export const number = (value) => {
  if (sinDato(value)) return "—";
  const n = Number(value);
  return Number.isFinite(n) ? NUM.format(n) : "—";
};

/**
 * Cantidad con su sustantivo concordado: `plural(3, "pedido")` → "3 pedidos".
 *
 * Escribir "3 pedido(s)" ahorra una línea de código y le cuesta una lectura
 * a cada persona que pasa por la pantalla. El plural irregular se pasa
 * aparte cuando no basta con añadir una "s".
 */
export const plural = (cantidad, singular, plural_ = `${singular}s`) => {
  const n = Number(cantidad) || 0;
  return `${number(n)} ${n === 1 ? singular : plural_}`;
};

export const percent = (value, digits = 1) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return `${n.toFixed(digits).replace(".0", "")}%`;
};

/**
 * Fecha en YYYY-MM-DD según el reloj DEL USUARIO, no en UTC.
 *
 * `toISOString().slice(0, 10)` es el atajo obvio y está mal para esto: pasa a
 * UTC, y en Colombia (UTC−5) eso adelanta el día a partir de las 7 p.m. Un
 * formulario abierto de noche proponía como fecha de inicio la de mañana, así
 * que la campaña se guardaba sin arrancar y el panel la mostraba activa sin
 * entregar nada. Se arma a mano en vez de con `toLocaleDateString` para no
 * depender de qué formato produzca cada configuración regional.
 */
const dosCifras = (n) => String(n).padStart(2, "0");

export const isoLocal = (fecha = new Date()) =>
  `${fecha.getFullYear()}-${dosCifras(fecha.getMonth() + 1)}-${dosCifras(fecha.getDate())}`;

/** La misma fecha local desplazada N días, para vigencias por defecto. */
export const isoEnDias = (dias) =>
  isoLocal(new Date(Date.now() + dias * 86400000));

/**
 * MySQL entrega "2026-08-14 22:31:05". Safari no parsea ese formato con el
 * espacio, así que se normaliza a ISO antes de construir la fecha.
 */
export const parseDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;
  const d = new Date(String(value).replace(" ", "T"));
  return Number.isNaN(d.getTime()) ? null : d;
};

export const date = (value) => {
  const d = parseDate(value);
  if (!d) return "—";
  return d.toLocaleDateString("es-CO", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

/**
 * Fecha compacta para celdas de tabla: "13 ago 2026".
 * La forma larga ("13 de ago de 2026") se parte en tres líneas dentro de una
 * columna angosta y engorda toda la fila.
 */
export const dateShort = (value) => {
  const d = parseDate(value);
  if (!d) return "—";
  return d
    .toLocaleDateString("es-CO", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    })
    .replace(/ de /g, " ");
};

export const dateTime = (value) => {
  const d = parseDate(value);
  if (!d) return "—";
  return d.toLocaleString("es-CO", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

/** "hace 5 minutos", "hace 3 días". Para columnas de actividad. */
export const relative = (value) => {
  const d = parseDate(value);
  if (!d) return "—";
  const diff = (d.getTime() - Date.now()) / 1000;
  const rtf = new Intl.RelativeTimeFormat("es-CO", { numeric: "auto" });
  const steps = [
    [60, "second", 1],
    [3600, "minute", 60],
    [86400, "hour", 3600],
    [604800, "day", 86400],
    [2629800, "week", 604800],
    [31557600, "month", 2629800],
    [Infinity, "year", 31557600],
  ];
  for (const [limit, unit, div] of steps) {
    if (Math.abs(diff) < limit) return rtf.format(Math.round(diff / div), unit);
  }
  return date(value);
};

/**
 * Duración legible entre dos marcas de tiempo ("18 min", "2 h 05", "3 d").
 * Devuelve null si falta alguna: estimarla inventaría un dato que no existe.
 */
export const duration = (desde, hasta) => {
  const a = parseDate(desde);
  const b = parseDate(hasta);
  if (!a || !b) return null;

  const min = Math.round((b.getTime() - a.getTime()) / 60000);
  if (min < 0) return null;
  if (min < 60) return `${min} min`;

  const horas = Math.floor(min / 60);
  if (horas < 24) return `${horas} h ${String(min % 60).padStart(2, "0")}`;

  return `${Math.floor(horas / 24)} d ${horas % 24} h`;
};

/** Horas transcurridas desde una fecha. Para detectar pedidos estancados. */
export const hoursSince = (value) => {
  const d = parseDate(value);
  if (!d) return null;
  return (Date.now() - d.getTime()) / 3_600_000;
};

/**
 * Palabras que no aportan a unas iniciales.
 *
 * Sin esto, "Supermercado La Esquina" daba "SL" y "Tienda el progreso" daba
 * "TE": dos negocios distintos con la misma inicial invisible y ningún avatar
 * reconocible. Los nombres de negocio en español están llenos de artículos.
 */
const RELLENO = new Set([
  "el",
  "la",
  "los",
  "las",
  "un",
  "una",
  "de",
  "del",
  "y",
  "e",
  "al",
  "a",
  "en",
  "con",
  "por",
  "para",
]);

/** Iniciales para los avatares sin foto. */
export const initials = (name) => {
  const partes = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  if (!partes.length) return "?";

  // Si TODO el nombre es relleno ("La Y"), se usa tal cual: unas iniciales
  // vacías serían peor que unas mediocres.
  const utiles = partes.filter((p) => !RELLENO.has(p.toLowerCase()));
  const usar = utiles.length ? utiles : partes;

  return (usar[0][0] + (usar[1]?.[0] ?? "")).toUpperCase();
};

/** Recorta sin partir palabras a la mitad. */
export const truncate = (text, max = 60) => {
  const s = String(text ?? "");
  if (s.length <= max) return s;
  return (
    s.slice(0, s.lastIndexOf(" ", max) > 0 ? s.lastIndexOf(" ", max) : max) +
    "…"
  );
};

/** Variación porcentual entre dos periodos, tolerante al divisor cero. */
export const delta = (current, previous) => {
  const a = Number(current) || 0;
  const b = Number(previous) || 0;
  if (b === 0) return a === 0 ? 0 : 100;
  return ((a - b) / Math.abs(b)) * 100;
};
